import { CoreEvent, eventNames } from '@carvana/core-events';
import { Clickable } from '@carvana/merch-components';
import { DispatcherEvent, NavPayloadAction } from '@carvana/merch-native';
import { useEffectOnce, useSubsequentEffect } from '@carvana/merch-react-hooks';
import { Fragment, JSX, memo, useContext, useState } from 'react';
import AnchorButton from '@/common/AnchorButton';
import Sections from '~/constants/home/sections';
import { useIsNative } from '~/context/common/DeviceContext';
import GlobalContext from '~/context/home/GlobalContext';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import { SavedSearch } from '~/contracts/common';
import { RecentSearch } from '~/contracts/home';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import formatEvent from '~/utilities/formatEvent';
import SearchLink from './SearchLink';
import SuggestedLinks from './SuggestedLinks';
import useGetSRPLinksToShow, { SRPLinkTitles } from './useGetSRPLinksToShow';
import { SuggestedSearch } from './useGetSuggestedSearches';
import {
  DividerLine,
  Header,
  HeaderWrapper,
  LinkCardsContainer,
  LinkCardTrackWrapper
} from '../LinkCards.styles';
import LinksLoadingCard, { LinkCardType } from '../LinksLoadingCard';

function SRPLinks() {
  const {
    header,
    searches,
    showLoading,
    showLink = false
  } = useGetSRPLinksToShow();
  const {
    recentSearches: { data: recentSearches }
  } = useContext(RecentSearchesContext);
  const { refreshSrpLinksTrigger } = useContext(GlobalContext);
  const [linksToShow, setLinksToShow] = useState<
    SavedSearch[] | RecentSearch[] | SuggestedSearch[]
  >([]);
  const [hasLink, setHasLink] = useState(false);
  const [headerToShow, setHeaderToShow] = useState<SRPLinkTitles>(
    SRPLinkTitles.None
  );
  const [isLoaded, setIsLoaded] = useState(false);

  const isNative = useIsNative();

  //So that we don't swap searches when a user saves a search
  useEffectOnce(
    () => {
      setLinksToShow(searches);
      setHasLink(showLink);
      setHeaderToShow(header);
      setIsLoaded(true);
    },
    [header, searches, showLink],
    !showLoading
  );

  function getCustomAttributes() {
    let type: string;
    switch (headerToShow) {
      case SRPLinkTitles.Saved:
        type = 'saved';
        break;
      case SRPLinkTitles.Recent:
        type = 'recent';
        break;
      case SRPLinkTitles.Suggested:
        type = 'suggested';
        break;
      default:
        type = 'suggested';
        break;
    }

    return {
      description: 'Search Card',
      type
    };
  }

  // Update recent searches when they are added to saved searches without swapping card to saved searches
  useSubsequentEffect(
    () => {
      if (headerToShow === SRPLinkTitles.Recent) {
        setLinksToShow(recentSearches.slice(0, 4));
      }
    },
    [recentSearches, headerToShow],
    isLoaded
  );

  useSubsequentEffect(() => {
    if (refreshSrpLinksTrigger) {
      setLinksToShow(searches);
      setHasLink(showLink);
      setHeaderToShow(header);
    }
  }, [searches, showLink, refreshSrpLinksTrigger, header]);

  function renderLinks(): JSX.Element | null {
    switch (headerToShow) {
      case SRPLinkTitles.Saved:
        return (
          <LinkCardsContainer>
            {(linksToShow as SavedSearch[]).map((link, index) => (
              <Fragment key={link.cvnaId}>
                {index !== 0 && <DividerLine />}
                <SearchLink key={link.savedSearchId} link={link} type="saved" />
              </Fragment>
            ))}
          </LinkCardsContainer>
        );
      case SRPLinkTitles.Recent:
        return (
          <LinkCardsContainer>
            {(linksToShow as RecentSearch[]).map((link, index) => (
              <Fragment key={link.cvnaId}>
                {index !== 0 && <DividerLine />}
                <SearchLink link={link} type="recent" />
              </Fragment>
            ))}
          </LinkCardsContainer>
        );
      case SRPLinkTitles.Suggested:
        return <SuggestedLinks links={linksToShow as SuggestedSearch[]} />;
      default:
        return null;
    }
  }

  return isLoaded ? (
    <LinkCardTrackWrapper
      data-testid="srp-link-card"
      event={formatEvent('Home feed card', {
        action: 'viewed',
        ...getCustomAttributes()
      })}
      trackOnce
    >
      <HeaderWrapper>
        <Header data-testid="srp-link-card-header">{headerToShow}</Header>
        {hasLink && (
          <Clickable
            element={AnchorButton}
            className="text-text-primary t-label-sm"
            data-analytics-id="Home feed card"
            data-analytics-attributes={{
              description: 'Search Card',
              type: 'saved',
              item: 'view all'
            }}
            data-testid="manage-saved-searches-link"
            onClick={() => {
              if (isNative) {
                sendMessageToNative(DispatcherEvent.Link, {
                  action: NavPayloadAction.ViewSavedSearches
                });
              } else {
                CoreEvent.emit(eventNames.favorites.mfe.drawer.open, {
                  tab: 'searches'
                });
              }
            }}
          >
            View All
          </Clickable>
        )}
      </HeaderWrapper>
      {renderLinks()}
    </LinkCardTrackWrapper>
  ) : (
    <LinksLoadingCard card={LinkCardType.SRP} />
  );
}

export default withErrorBoundary(memo(SRPLinks), {
  section: Sections.SRPLinks
});
